import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { createApp, type AppCreateRequest } from '../../utils/app';
import { ArrowLeft, RefreshCw, Smartphone } from 'lucide-react';

// 앱 생성 시에는 대기중만 선택 가능
const STATUS_OPTIONS_CREATE = [{ value: 'PENDING', label: '대기중' }];

const TYPE_OPTIONS = [
  { value: 'CUSTOMER', label: '고객용' },
  { value: 'AGENT', label: '에이전트' },
];

interface FormErrors {
  code?: string;
  status?: string;
  type?: string;
  name?: string;
}

const FormRow: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string;
}> = ({ label, required, children, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="w-52 sm:w-56 flex-shrink-0">
      <p className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
    </div>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  </div>
);

const OptionButtons: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ options, value, onChange, disabled }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => !disabled && onChange(opt.value)}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <span className={`w-2 h-2 rounded-full ${selected ? 'bg-white' : 'bg-gray-400'}`} />
          {opt.label}
        </button>
      );
    })}
  </div>
);

const AppCreate: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl } = useParams<{ projectCode: string }>();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('app-list');

  const [formData, setFormData] = useState({
    code: '',
    status: 'PENDING',
    type: 'CUSTOMER',
    name: '',
    note: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!formData.code.trim()) next.code = '코드는 필수입니다.';
    else if (/\s/.test(formData.code)) next.code = '코드에는 공백을 넣을 수 없습니다.';
    if (!formData.status) next.status = '상태를 선택하세요.';
    if (!formData.type) next.type = '타입을 선택하세요.';
    if (!formData.name.trim()) next.name = '이름은 필수입니다.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate() || !projectCodeFromUrl) return;
    try {
      setIsSubmitting(true);
      const payload: AppCreateRequest = {
        code: formData.code.trim(),
        status: formData.status,
        type: formData.type,
        name: formData.name.trim(),
      };
      if (formData.note.trim()) payload.note = formData.note.trim();
      await createApp(projectCodeFromUrl, payload);
      navigate(`/projects/${projectCodeFromUrl}/apps`, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '앱 생성에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      code: '',
      status: 'PENDING',
      type: 'CUSTOMER',
      name: '',
      note: '',
    });
    setErrors({});
    setSubmitError(null);
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="앱 생성"
          subtitle={projectCodeFromUrl ? `${projectDetail.name} (${projectCodeFromUrl})` : '새 앱을 등록합니다'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                type="button"
                onClick={() => navigate(`/projects/${projectCodeFromUrl}/apps`)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                앱 목록으로
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {submitError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {submitError}
                </div>
              )}

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    기본 정보
                  </h2>
                </div>
                <div className="px-4 lg:px-6 py-2 space-y-0">
                  <FormRow label="코드" required error={errors.code}>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => { setFormData((p) => ({ ...p, code: e.target.value })); setErrors((e2) => ({ ...e2, code: undefined })); }}
                      placeholder="공백 없이 입력"
                      className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.code ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </FormRow>
                  <FormRow label="상태" required error={errors.status}>
                    <div className="flex flex-wrap items-center gap-2">
                      <OptionButtons
                        options={STATUS_OPTIONS_CREATE}
                        value={formData.status}
                        onChange={(v) => setFormData((p) => ({ ...p, status: v }))}
                        disabled
                      />
                      <span className="text-sm text-gray-500">생성 시 상태는 대기중으로 설정됩니다.</span>
                    </div>
                  </FormRow>
                  <FormRow label="타입" required error={errors.type}>
                    <OptionButtons
                      options={TYPE_OPTIONS}
                      value={formData.type}
                      onChange={(v) => setFormData((p) => ({ ...p, type: v }))}
                    />
                  </FormRow>
                  <FormRow label="이름" required error={errors.name}>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => { setFormData((p) => ({ ...p, name: e.target.value })); setErrors((e2) => ({ ...e2, name: undefined })); }}
                      placeholder="앱 이름"
                      className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </FormRow>
                  <FormRow label="비고">
                    <textarea
                      value={formData.note}
                      onChange={(e) => setFormData((p) => ({ ...p, note: e.target.value }))}
                      placeholder="선택 입력"
                      rows={3}
                      className="block w-full min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    />
                  </FormRow>
                </div>
              </div>

              {/* 하단 버튼 영역 - 폼 섹션 바깥 (상품 생성 화면과 동일) */}
              <div className="flex justify-between items-center mt-6">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium text-gray-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/projects/${projectCodeFromUrl}/apps`)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm font-medium"
                  >
                    {isSubmitting ? '생성 중...' : '앱 생성'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppCreate;